import Vue from "vue";
import Vuex from "vuex";
import { router } from "@/router";
import { publicApi, api } from "@/api";
import * as socket from "@/socket";

Vue.use(Vuex);

const authStore = {
  state: {
    idToken: null,
    userId: null,
    user: null,
    email: null,
    groupName: null,
    groupId: null,
    organisationName: null,
    organisationId: null,
    storeId: null,
    storeName: null,
    storeInfo: null,
    isAdmin: false,
    availablesStores: [],
    orgsAvailable: [],
    image: null,
    timerWarning: false,
    permissionLevel: 0,
    loginTimeStamp: null,
    authHeader: {},
    customerId: null
  },
  getters: {
    getIdToken: function(state) {
      return state.idToken;
    },
    getAuthHeader(state) {
      return state.authHeader;
    },
    getAuthToken(state) {
      return state.idToken;
    },
    getUserSettings(state) {
      return {
        organisationId: state.organisationId,
        organisationName: state.organisationName,
        storeId: state.storeId,
        storeName: state.storeName,
        storeInfo: state.storeInfo,
        userName: state.user,
        email: state.email,
        availablesStores: state.availablesStores,
        orgsAvailable: state.orgsAvailable
      };
    },
    getUserName(state) {
      return state.user;
    },
    getUserLevel(state) {
      return state.permissionLevel;
    },
    getIsAdmin(state) {
      return state.isAdmin;
    },
    getOrgGroupId(state) {
      return state.groupId;
    },
    getStoreId(state) {
      return state.storeId;
    },
    getOrganisationId(state) {
      return state.organisationId;
    },
    getOrganisationName(state) {
      if (
        state.orgsAvailable.find(
          x => x.id_organisation === state.organisationId
        )
      ) {
        return state.orgsAvailable.find(
          x => x.id_organisation === state.organisationId
        ).name_organisation;
      } else {
        return "";
      }
    },
    getStoreInfo(state) {
      return state.storeInfo;
    },
    getStoreName(state) {
      if (state.storeInfo) {
        return state.storeInfo.name_store;
      } else {
        return "";
      }
    },
    getTimerWarning(state) {
      return state.timerWarning;
    },
    getTokenTimeStamp(state) {
      return state.loginTimeStamp;
    },
    getAvailablesStores(state) {
      return state.availablesStores;
    },
    getStoresAvailables(state) {
      return state.storesAvailable;
    },
    getOrganisationsAvailables(state) {
      return state.orgsAvailable;
    },
    getCustomerId(state) {
      return state.customerId;
    }
  },
  mutations: {
    authUser(state, userData) {
      state.idToken = userData.token;
      state.userId = userData.userId;
      state.user = userData.username;
      state.email = userData.email;
      state.groupId = userData.groupId;
      state.groupName = userData.groupName;
      state.organisationName = userData.organisationName;
      state.organisationId = userData.organisationId;
      state.storeName = userData.storeName;
      state.storeId = userData.storeId;
      state.storeInfo = userData.storeInfo;
      state.availablesStores = userData.availablesStores;
      state.orgsAvailable = userData.orgsAvailable;
      state.timerWarning = false;
      state.loginTimeStamp = Date.now();
      state.permissionLevel = userData.permissionLevel;
      state.authHeader = {
        headers: { Authorization: `JWT ${userData.token}` }
      };
      state.customerId = userData.customerId;
    },
    clearAuthData(state) {
      state.timerWarning = true;
      state.loginTimeStamp = null;
      state.idToken = null;
      state.userId = null;
      state.user = null;
      state.email = null;
      state.groupId = null;
      state.groupName = null;
      state.organisationName = null;
      state.organisationId = null;
      state.storeName = null;
      state.storeId = null;
      state.storeInfo = null;
      state.isAdmin = false;
      state.availablesStores = [];
      state.orgsAvailable = [];
      state.permissionLevel = 0;
      state.authHeader = {};
      state.customerId = null;
    },
    refreshToken(state, data) {
      state.idToken = data;
      state.loginTimeStamp = Date.now();
      state.authHeader = {
        headers: { Authorization: `JWT ${data}` }
      };
    },
    changeStoreId(state, value) {
      state.storeId = value;
      state.storeInfo = state.storesAvailable.find(x => x.id_store === value);
    },
    changeOrganisationId(state, value) {
      state.organisationId = value;
    },
    changeUserIsAdmin(state, payload) {
      state.isAdmin = payload;
    },
    setStoreKey(state, payload) {
      state.storeInfo.store_key = payload;
    },
    setStoreInfo(state, value) {
      state.storeInfo = value;
    },
    setStoreName(state, value) {
      state.storeInfo.name_store = value;
      state.storeName = value;
    },
    setUsername(state, value) {
      state.user = value;
    },
    setUserEmail(state, value) {
      state.email = value;
    }
  },
  actions: {
    setOrganisation({ commit }, data) {
      commit("changeOrganisationId", data);
    },
    setStore({ commit }, data) {
      commit("changeStoreId", data);
    },
    setIsAdmin({ commit }, data) {
      commit("changeUserIsAdmin", data);
    },
    async refreshToken({ commit, rootState }) {
      const res = await publicApi.refreshToken(rootState.authStore.idToken);
      commit("authUser", {
        token: res.token,
        userId: res.user.id,
        username: res.user.username,
        email: res.user.email ? res.user.email : null,
        organisationName: res.organisation.name_organisation,
        organisationId: res.userSettings.organisation,
        storeName: res.store.name_store,
        storeInfo: res.store,
        storeId: res.userSettings.store,
        permissionLevel: res.userSettings.permission_level,
        availablesStores: res.storesAvailable,
        orgsAvailable: res.orgsAvailable,
        groupId: res.userSettings.org_group,
        groupName: res.group.name,
        customerId: res.userSettings.stripe_customer_id
      });
      commit("refreshToken", res.token);
    },
    timeoutToken({ commit }, data) {
      commit("clearAuthData", data);
    },
    async login({ commit }, authData) {
      try {
        const res = await publicApi.login(authData);
        commit("authUser", {
          token: res.token,
          userId: res.user.id,
          username: res.user.username,
          email: res.user.email ? res.user.email : null,
          organisationName: res.organisation.name_organisation,
          organisationId: res.userSettings.organisation,
          storeName: res.store.name_store,
          storeInfo: res.store,
          storeId: res.userSettings.store,
          permissionLevel: res.userSettings.permission_level,
          availablesStores: res.storesAvailable,
          orgsAvailable: res.orgsAvailable,
          groupId: res.userSettings.org_group,
          groupName: res.group.name,
          customerId: res.userSettings.stripe_customer_id
        });
        router.push({ name: "StatisticsIndex" });
      } catch (error) {
        commit("clearAuthData");
        socket.disconnect();
        throw error;
      }
    },
    async adminLogin({ commit }, data) {
      try {
        await api.postAdminLogin(data);
        commit("changeUserIsAdmin", true);
      } catch (error) {
        return Promise.reject(error.response);
      }
    },
    adminLogout({ commit }) {
      commit("changeUserIsAdmin", false);
    },
    logout({ commit }) {
      commit("clearAuthData");
      socket.disconnect();
      localStorage.removeItem("token");
      router.replace("/");
    }
  }
};

export default authStore;
